import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { ObjectType, Platform, PopularTitlesEdge, SimilarTitlesEdge, TitleFilter } from '@/@types/graphql-types';
import {
	GetSimilarTitlesDocument,
	GetSimilarTitlesQuery,
	GetSimilarTitlesQueryVariables,
	GetSimilarUpcomingTitlesDocument,
	GetSimilarUpcomingTitlesQueryVariables,
	SimilarTitleFragment,
} from '@/components/graphql/queries/GetSimilarTitles.query';
import { SponsoredAdFragmentFragment } from '@/pages/graphql/queries/GetPopularTitles.query';

const App = namespace('app');
const Language = namespace('language');
const User = namespace('user');

@Component({
	name: 'SimilarTitlesProvider',
	apollo: {
		similarTitles: {
			query() {
				return this.isUpcoming ? GetSimilarUpcomingTitlesDocument : GetSimilarTitlesDocument;
			},
			variables(): GetSimilarTitlesQueryVariables | GetSimilarUpcomingTitlesQueryVariables {
				return this.queryVariables;
			},
			update(data) {
				if (this.isUpcoming) {
					return data?.newTitles?.edges.map((edge: PopularTitlesEdge) => edge.node) ?? [];
				}

				this.sponsoredAd = data.node.similarTitlesV2.sponsoredAd;
				return data.node.similarTitlesV2.edges.map(((edge: SimilarTitlesEdge) => edge.node) ?? []);
			},
		},
		/**
		 * SponsoredRecommendation special case: SeoUser
		 * we need to send the request from the client in order to have a meaningful SR bidding/tracking
		 * Manually overwrite the titles list
		 */
		sponsoredAd: {
			query: GetSimilarTitlesDocument,
			fetchPolicy: 'no-cache',
			prefetch: false,
			variables(): GetSimilarTitlesQueryVariables {
				return this.queryVariables;
			},
			update({ node }) {
				this.isSeoSRLoaded = true;

				const { similarTitlesV2 } = node;
				const { sponsoredAd, edges } = similarTitlesV2;

				if (edges?.length && !sponsoredAd?.holdoutGroup) {
					this.$apollo.getClient().writeQuery<GetSimilarTitlesQuery, GetSimilarTitlesQueryVariables>({
						variables: this.queryVariables,
						query: GetSimilarTitlesDocument,
						data: { node },
					});
				}

				return sponsoredAd;
			},
			skip() {
				return this.isUpcoming || !this.isSeoUser || this.isSeoSRLoaded;
			},
		},
	},
})
export default class SimilarTitlesProvider extends Vue {
	@Prop({ required: true }) titleId: string;
	@Prop({ required: true }) titleObjectType: ObjectType;
	@Prop({ default: null }) upcomingFilters: Pick<TitleFilter, 'isUpcoming' | 'genres' | 'objectTypes'>;

	@App.Getter isSeoUser: boolean;

	@Language.Getter country: string;
	@Language.Getter language: string;

	@User.Getter isPremium: boolean;

	isSeoSRLoaded = false;
	similarTitles: SimilarTitleFragment[] = [];
	sponsoredAd: SponsoredAdFragmentFragment = {} as SponsoredAdFragmentFragment;

	get isUpcoming() {
		return !!this.upcomingFilters;
	}

	get isEmpty() {
		return this.similarTitles?.length === 0;
	}

	get queryVariables() {
		const variables = {
			language: this.language,
			country: this.country,
			filters: {
				excludeIrrelevantTitles: this.isPremium,
			},
		};

		// titleId is only required for SimilarTitles query, not SimilarUpcomingTitles
		if (this.isUpcoming) {
			variables.filters = { ...variables.filters, ...this.upcomingFilters };
			return variables as GetSimilarUpcomingTitlesQueryVariables;
		}

		(<GetSimilarTitlesQueryVariables>variables).titleId = this.titleId;
		(<GetSimilarTitlesQueryVariables>variables).allowSponsoredRecommendations = {
			country: this.country,
			language: this.language,
			platform: Platform.Web,
			pageType: this.titleObjectType?.toLowerCase() || undefined,
		};

		return <GetSimilarTitlesQueryVariables>variables;
	}

	render() {
		if (this.$scopedSlots.default) {
			return this.$scopedSlots.default({
				titles: this.similarTitles,
				isEmpty: this.isEmpty,
				sponsoredAd: this.sponsoredAd?.campaign ? this.sponsoredAd : null,
			});
		}

		return this.$slots[0];
	}
}
